import { useState, useEffect, useCallback } from 'react'
import type { Offer, SellOfferPosition } from '../../types/offer'
import type { InventoryCard } from '../../types/inventory'
import { getOffer, updateOffer, addSellPosition, updateSellPosition, deleteSellPosition } from '../../services/offerService'
import { getInventory } from '../../services/inventoryService'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Loader2, Trash2, ImageIcon, Pencil } from 'lucide-react'
import { PokemonCardModal } from '../PokemonCardModal'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card'
import { PriceEditor } from '../inventory/PriceEditor'
import { AddCard } from './AddCard'
import type { FullPokemonCard } from '@/types/pokemon'

interface SellOfferEditProps {
  offerId: string
  onSaved?: () => void
}

function formatCurrency(value: number | null | undefined) {
  if (value == null) return '—'
  return `$${value.toFixed(2)}`
}

function getCardDisplayName(invCard: InventoryCard | undefined, position: SellOfferPosition): string {
  if (invCard?.card?.name) return invCard.card.name
  return position.inventoryCard?.cardId ?? position.inventoryCardId
}

export function SellOfferEdit({ offerId, onSaved }: SellOfferEditProps) {
  const [offer, setOffer] = useState<Offer | null>(null)
  const [inventory, setInventory] = useState<InventoryCard[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [editingPriceId, setEditingPriceId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [viewingCard, setViewingCard] = useState<FullPokemonCard | null>(null)
  const [title, setTitle] = useState('')
  const [status, setStatus] = useState('active')
  const [isSavingOffer, setIsSavingOffer] = useState(false)

  const loadData = useCallback(async () => {
    if (!offerId) return
    setIsLoading(true)
    setError(null)
    try {
      const [offerData, invData] = await Promise.all([getOffer(offerId), getInventory()])
      if (offerData.type !== 'Sell') {
        setError('This offer is not a Sell offer')
        setOffer(null)
        return
      }
      setOffer(offerData)
      setTitle(offerData.title ?? '')
      setStatus(offerData.status)
      setInventory(invData.cards ?? [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load offer')
      setOffer(null)
    } finally {
      setIsLoading(false)
    }
  }, [offerId])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleSaveOffer = async () => {
    if (!offer) return
    setIsSavingOffer(true)
    setError(null)
    try {
      const updated = await updateOffer(offer.id, { title: title.trim() || null, status })
      setOffer(updated)
      onSaved?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update offer')
    } finally {
      setIsSavingOffer(false)
    }
  }

  const handlePriceSave = async (position: SellOfferPosition, priceString: string) => {
    const price = parseFloat(priceString)
    if (isNaN(price) || price < 0) return
    try {
      await updateSellPosition(position.id, price)
      setOffer((prev) =>
        prev
          ? {
              ...prev,
              sellPositions: prev.sellPositions?.map((p) => (p.id === position.id ? { ...p, price } : p)) ?? [],
            }
          : null,
      )
      setEditingPriceId(null)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to update price')
    }
  }

  const handleDeletePosition = async (position: SellOfferPosition) => {
    if (!confirm('Remove this card from the offer?')) return
    setDeletingId(position.id)
    try {
      await deleteSellPosition(position.id)
      setOffer((prev) =>
        prev
          ? {
              ...prev,
              sellPositions: prev.sellPositions?.filter((p) => p.id !== position.id) ?? [],
            }
          : null,
      )
      onSaved?.()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove position')
    } finally {
      setDeletingId(null)
    }
  }

  const handleAddPosition = async (cardId: string, price: number) => {
    if (!offer) return
    setError(null)
    try {
      const updated = await addSellPosition(offer.id, cardId, price)
      setOffer(updated)
      onSaved?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add card')
      throw err
    }
  }

  const positions = offer?.sellPositions ?? []
  const positionInventoryIds = new Set(positions.map((p) => p.inventoryCardId))
  const availableCards = inventory.filter((c) => !positionInventoryIds.has(c.id))

  const totalValue = positions.reduce((sum, p) => sum + (p.price ?? 0), 0)

  return (
    <Card className="min-h-[400px]">
      <CardHeader>
        <CardTitle className="text-3xl text-blue-800">Edit Sell Offer</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading && (
          <div className="flex items-center justify-center py-12 gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="text-muted-foreground">Loading...</span>
          </div>
        )}

        {error && !isLoading && (
          <p className="text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {offer && !isLoading && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="offer-title" className="text-gray-700">
                  Title
                </Label>
                <Input
                  id="offer-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  onBlur={handleSaveOffer}
                  placeholder="Untitled offer"
                  className="mt-1.5 bg-white border-gray-300"
                  disabled={isSavingOffer}
                />
              </div>
              <div>
                <Label htmlFor="offer-status" className="text-gray-700">
                  Status
                </Label>
                <Select
                  value={status}
                  onValueChange={async (v) => {
                    setStatus(v)
                    try {
                      const updated = await updateOffer(offer.id, { status: v })
                      setOffer(updated)
                      onSaved?.()
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to update status')
                    }
                  }}
                >
                  <SelectTrigger id="offer-status" className="mt-1.5 bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-gray-700">Positions ({positions.length})</Label>
                <span className="text-sm font-medium text-gray-900">Total: {formatCurrency(totalValue)}</span>
              </div>

              {positions.length === 0 ? (
                <p className="text-muted-foreground text-sm py-4">No cards in this offer yet.</p>
              ) : (
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-14">Image</TableHead>
                        <TableHead>Card</TableHead>
                        <TableHead>Condition</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead className="w-12">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {positions.map((position) => {
                        const invCard = inventory.find((c) => c.id === position.inventoryCardId)
                        return (
                          <TableRow key={position.id}>
                            <TableCell>
                              <button
                                type="button"
                                onClick={() => invCard?.card && setViewingCard(invCard.card!)}
                                className="w-12 h-16 rounded overflow-hidden bg-muted border flex items-center justify-center shrink-0 hover:ring-2 hover:ring-blue-400 cursor-pointer"
                              >
                                {invCard?.card?.cardData.imageUrl ? (
                                  <img
                                    src={invCard.card.cardData.imageUrl}
                                    alt=""
                                    className="w-full h-full object-contain"
                                    onError={(e) => {
                                      ;(e.target as HTMLImageElement).style.display = 'none'
                                    }}
                                  />
                                ) : (
                                  <ImageIcon className="h-6 w-6 text-muted-foreground" />
                                )}
                              </button>
                            </TableCell>
                            <TableCell>
                              <button
                                type="button"
                                onClick={() => invCard?.card && setViewingCard(invCard.card!)}
                                className="text-left font-medium text-gray-900 hover:text-blue-600 hover:underline cursor-pointer"
                              >
                                {getCardDisplayName(invCard, position)}
                              </button>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{invCard?.condition ?? position.inventoryCard?.condition ?? '—'}</TableCell>
                            <TableCell className="text-muted-foreground">{invCard?.storageLocation?.name ?? position.inventoryCard?.storageLocation?.name ?? '—'}</TableCell>
                            <TableCell>
                              {editingPriceId === position.id ? (
                                <PriceEditor initialValue={position.price} onSave={(price) => handlePriceSave(position, price)} onCancel={() => setEditingPriceId(null)} />
                              ) : (
                                <div className="flex items-center gap-2 group cursor-pointer w-fit" onClick={() => setEditingPriceId(position.id)}>
                                  <span>{formatCurrency(position.price)}</span>
                                  <Pencil className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDeletePosition(position)}
                                disabled={deletingId === position.id}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                aria-label="Remove"
                              >
                                {deletingId === position.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                              </Button>
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {availableCards.length > 0 && <AddCard availableCards={availableCards} onAdd={handleAddPosition} />}
            </div>
          </>
        )}
      </CardContent>
      <PokemonCardModal card={viewingCard} isOpen={!!viewingCard} onClose={() => setViewingCard(null)} />
    </Card>
  )
}
